import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Link2, EyeOff } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { COMPANY_LABEL } from "./OpsLayout";

export default function ReviewQueue({ onResolved }) {
  const [items, setItems] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [pick, setPick] = useState({});

  const load = async () => {
    try { const [q, d] = await Promise.all([api.get("/ops/review-queue"), api.get("/ops/drivers")]); setItems(q.data); setDrivers(d.data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const resolve = async (it, action) => {
    try {
      await api.post(`/ops/review-queue/${it.id}/resolve`, { action, driver_id: pick[it.id] || null });
      toast.success(action === "create" ? "Driver created and records linked" : action === "link" ? "Linked to driver" : "Ignored");
      load(); onResolved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const createAll = async () => {
    try { const { data } = await api.post("/ops/review-queue/create-all"); toast.success(`${data.created} driver(s) created`); load(); onResolved?.(); } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!items.length) return null;
  return (
    <div data-testid="ops-review-queue" className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div><h2 className="text-lg font-bold uppercase display text-white">Match review queue</h2><p className="text-xs text-slate-400 mt-1">{items.length} name(s) from reports don't match the roster. Link to an existing driver or create a new one — their report rows are re-linked automatically.</p></div>
        <Button size="sm" data-testid="ops-review-create-all" onClick={createAll} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold"><UserPlus className="w-4 h-4 mr-1" /> Create all as new drivers</Button>
      </div>
      <div className="mt-4 space-y-2">
        {items.map((it) => (
          <div key={it.id} data-testid={`ops-review-item-${it.id}`} className="rounded-lg border border-slate-800 bg-slate-950 p-3 grid md:grid-cols-[1fr_240px_auto] gap-3 items-center">
            <div className="min-w-0"><div className="font-semibold text-white">{it.raw_name} {it.fedex_id && <span className="font-mono text-xs text-slate-400">#{it.fedex_id}</span>}</div>
              <div className="text-[11px] text-slate-500">{COMPANY_LABEL[it.company_id]} · {(it.report_types || []).join(", ")} · {it.rows} row(s) · {(it.dates || []).join(", ")}</div></div>
            <Select value={pick[it.id] || ""} onValueChange={(v) => setPick({ ...pick, [it.id]: v })}>
              <SelectTrigger data-testid={`ops-review-pick-${it.id}`} className="bg-slate-900 border-slate-700 text-white text-xs"><SelectValue placeholder="Link to existing driver…" /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-white max-h-72">{[...drivers].sort((a, b) => (a.company_id === it.company_id ? -1 : b.company_id === it.company_id ? 1 : 0)).map((d) => <SelectItem key={d.id} value={d.id}>{d.full_name}{d.fedex_id ? ` (#${d.fedex_id})` : ""}{d.company_id !== it.company_id ? ` — ${COMPANY_LABEL[d.company_id]} driver` : ""}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex gap-1 justify-end">
              <Button size="sm" variant="outline" data-testid={`ops-review-link-${it.id}`} disabled={!pick[it.id]} onClick={() => resolve(it, "link")} className="border-slate-700 text-slate-200"><Link2 className="w-4 h-4 mr-1" /> Link</Button>
              <Button size="sm" data-testid={`ops-review-create-${it.id}`} onClick={() => resolve(it, "create")} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold"><UserPlus className="w-4 h-4 mr-1" /> Create</Button>
              <Button size="sm" variant="ghost" data-testid={`ops-review-ignore-${it.id}`} onClick={() => resolve(it, "ignore")} className="text-slate-400"><EyeOff className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
